import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  discountReductionLabel,
  type DiscountQuote,
  type PublicMembershipLevel,
} from '@/pages/PublicJoinFormPage.types'

export function DiscountCodeSection({
  level,
  code,
  quote,
  pending,
  error,
  onCodeChange,
  onApply,
}: {
  level: PublicMembershipLevel
  code: string
  quote: DiscountQuote | null
  pending: boolean
  error?: string
  onCodeChange: (value: string) => void
  onApply: () => void
}) {
  return (
    <section
      className="space-y-3 rounded-md border border-border bg-card p-4"
      aria-labelledby="discount-code-heading"
    >
      <div>
        <h2 id="discount-code-heading" className="text-body font-semibold text-foreground">
          Discount Code
        </h2>
        <p className="mt-1 text-caption text-muted-foreground">
          Optional for this paid Membership Level.
        </p>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="join-discount-code" className="font-normal">
            Discount Code
          </Label>
          <Input
            id="join-discount-code"
            className="mt-1"
            value={code}
            aria-invalid={Boolean(error)}
            onChange={(event) => onCodeChange(event.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={pending || code.trim() === ''}
          onClick={onApply}
        >
          {pending ? 'Applying...' : 'Apply Discount Code'}
        </Button>
      </div>
      {error && <p className="text-caption text-destructive">{error}</p>}
      <PriceSummary level={level} quote={quote} />
    </section>
  )
}

function PriceSummary({
  level,
  quote,
}: {
  level: PublicMembershipLevel
  quote: DiscountQuote | null
}) {
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-body-sm">
      <dt className="text-muted-foreground">Membership Level</dt>
      <dd className="text-right tabular-nums text-foreground">{quote?.gross ?? level.fee}</dd>
      {quote && (
        <>
          <dt className="text-muted-foreground">
            {quote.code} ({discountReductionLabel(quote)})
          </dt>
          <dd className="text-right tabular-nums text-foreground">−{quote.reduction}</dd>
        </>
      )}
      <dt className="font-medium text-foreground">Total</dt>
      <dd className="text-right font-medium tabular-nums text-foreground">
        {quote?.total ?? level.fee}
      </dd>
    </dl>
  )
}
